import type { Event } from '@sentry/react';

/** Where the Hub sends the browser back to, with the code and state in the query. */
export const HUB_CALLBACK_PATH = '/auth/callback';

const CALLBACK_QUERY = /(\/auth\/callback)\/?\?[^\s"#\\]*/g;

let stashedSearch: string | null = null;
/** Only a page load at the callback ever had the code and state in its URL. */
let loadedWithCallbackQuery = false;

const isCallbackPath = (pathname: string) =>
  pathname === HUB_CALLBACK_PATH || pathname === `${HUB_CALLBACK_PATH}/`;

/**
 * Takes the code and state out of the address bar before anything records the
 * URL: called first thing at startup, before Sentry starts (its page-load
 * transaction and replay would otherwise carry them). The callback page reads
 * them back with `takeStashedCallbackSearch`.
 */
export const stashCallbackSearch = (): void => {
  const { pathname, search } = window.location;
  if (!isCallbackPath(pathname) || !search) {
    return;
  }
  stashedSearch = search;
  loadedWithCallbackQuery = true;
  window.history.replaceState(window.history.state, '', HUB_CALLBACK_PATH);
};

/** The callback query stashed at startup, if any. Single-use. */
export const takeStashedCallbackSearch = (): string | null => {
  const search = stashedSearch;
  stashedSearch = null;
  return search;
};

/**
 * Drop the query string from every callback URL in a Sentry event.
 *
 * Defense in depth: the query is stripped before Sentry starts, but the page
 * load may still be recorded with the URL the Hub sent the browser back to
 * (its navigation timing, for one). Only a page load at the callback can carry
 * it, so every other event is returned untouched, without serializing it.
 */
export const scrubHubCallbackEvent = <T extends Event>(event: T): T => {
  if (!loadedWithCallbackQuery) {
    return event;
  }
  const serialized = JSON.stringify(event);
  // `search` ignores the global flag's lastIndex.
  if (!serialized || serialized.search(CALLBACK_QUERY) === -1) {
    return event;
  }

  const scrubbed: T = JSON.parse(serialized.replace(CALLBACK_QUERY, '$1'));
  if (scrubbed.request?.url?.includes(HUB_CALLBACK_PATH)) {
    delete scrubbed.request.query_string;
  }
  return scrubbed;
};
